// src/components/Modal.tsx
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  confirmText?: string;
  onConfirm?: () => void;
  staticBackdrop?: boolean;
  isActionBtn?: boolean;
  dialogClassName?: string;
  childrenClassName?: string;
  actionClass?: string;
  confirmBtnClass?: string;
  showBackButton?: boolean;
  isConfirming?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  confirmText = "Confirm",
  onConfirm,
  staticBackdrop = false,
  isActionBtn = true,
  dialogClassName = "",
  childrenClassName = "",
  actionClass = "",
  confirmBtnClass = "",
  showBackButton = false,
  isConfirming = false,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={staticBackdrop ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-90"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-90"
            >
              <Dialog.Panel
                className={`${
                  dialogClassName
                    ? dialogClassName
                    : "w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-dark-foreground shadow-xl rounded-2xl"
                }`}
              >
                {showBackButton && (
                  <div
                    className="flex flex-row items-center mb-3 cursor-pointer"
                    onClick={onClose}
                  >
                    <IoMdArrowRoundBack
                      size={20}
                      color="blue"
                      className="mt-0.5"
                    />{" "}
                    <span className="text-blue-500 ml-1">Back</span>
                  </div>
                )}

                {title && (
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {title}
                  </Dialog.Title>
                )}
                <div
                  className={
                    showBackButton
                      ? "mt-4 max-h-[65vh] overflow-y-auto text-sm text-gray-700 dark:text-gray-300"
                      : childrenClassName
                      ? childrenClassName
                      : "mt-4"
                  }
                >
                  {children}
                </div>

                <div
                  className={
                    actionClass
                      ? actionClass
                      : "mt-6 flex justify-end space-x-2"
                  }
                >
                  {isActionBtn ? (
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  ) : null}

                  {onConfirm && (
                    <button
                      type="button"
                      disabled={isConfirming}
                      className={`${
                        confirmBtnClass
                          ? confirmBtnClass
                          : isConfirming
                          ? "px-4 py-2 text-sm text-white bg-blue-400 rounded-md cursor-not-allowed"
                          : "px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      } font-medium focus:outline-none flex items-center justify-center gap-2`}
                      onClick={onConfirm}
                    >
                      {isConfirming && (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      {confirmText}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
